// The contract between a calendar that knows a free slot and a booking-creation
// screen that books it (#3039, M12): which object, from when, until when.
//
// Carried in the URL rather than in Redux, so the link survives a refresh and
// can be opened in a new tab — and so the creation screen's own reset on mount
// (`clearBookingCreationStates`) cannot wipe it before it is read.
//
// Instants travel as ISO strings and are turned into the screen's wall-clock
// fields (date + `HH:mm`) in the zone of the slot's location (#3219; the
// station default when not given), the same zone the occupancy grid draws that
// room in. Reading them in the browser's zone would prefill 17:00 for a slot
// the calendar showed at 18:00 for anyone outside it.

import dayjs from 'dayjs';
import { GRID_TZ } from '../../assets/components/utilizationRange';
import { getAvailableBookingTimes } from '../../../utils/dateTimeUtils';
import { stationCalendarDay, stationTimeOfDay } from '../../../utils/stationTime';

export const SLOT_PREFILL_PARAMS = {
    assetId: 'assetId',
    locationId: 'locationId',
    from: 'from',
    to: 'to',
} as const;

export interface SlotPrefillInput {
    assetId: string;
    /** Used to resolve the city the creation screen filters objects by. */
    locationId: string;
    /** ISO date-time, inclusive. */
    from: string;
    /** ISO date-time, exclusive. */
    to: string;
}

export interface SlotPrefill {
    assetId: string;
    locationId: string | null;
    /** `YYYY-MM-DD` in the slot location's zone. */
    startDate: string;
    /** `HH:mm` in the slot location's zone. */
    startTime: string;
    endDate: string;
    endTime: string;
}

/** The query string a calendar appends to a creation route. */
export const buildSlotPrefillSearch = (input: SlotPrefillInput): string =>
    new URLSearchParams({
        [SLOT_PREFILL_PARAMS.assetId]: input.assetId,
        [SLOT_PREFILL_PARAMS.locationId]: input.locationId,
        [SLOT_PREFILL_PARAMS.from]: input.from,
        [SLOT_PREFILL_PARAMS.to]: input.to,
    }).toString();

const wallClock = (instant: Date, timeZone: string): { date: string, time: string } => ({
    date: stationCalendarDay(instant, timeZone),
    time: stationTimeOfDay(instant, timeZone),
});

const parseInstant = (raw: string | null): Date | null => {
    if (!raw) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Read a prefill off a creation screen's query string, or `null` when there is
 * none or it is unusable. An unusable one is ignored rather than half-applied:
 * a form prefilled with a start but no end, or an end before its start, would
 * look deliberate. `timeZone` is the slot's location zone (the station default).
 */
export const parseSlotPrefill = (params: URLSearchParams, timeZone: string = GRID_TZ): SlotPrefill | null => {
    const assetId = params.get(SLOT_PREFILL_PARAMS.assetId);
    const from = parseInstant(params.get(SLOT_PREFILL_PARAMS.from));
    const to = parseInstant(params.get(SLOT_PREFILL_PARAMS.to));
    if (!assetId || !from || !to || to.getTime() <= from.getTime()) return null;

    const start = wallClock(from, timeZone);
    const end = wallClock(to, timeZone);
    return {
        assetId,
        locationId: params.get(SLOT_PREFILL_PARAMS.locationId) || null,
        startDate: start.date,
        startTime: start.time,
        endDate: end.date,
        endTime: end.time,
    };
};

/**
 * Would the venue create form take this slot as it stands?
 *
 * Asked of the form's OWN time rule — `getAvailableBookingTimes`, called the
 * way CreateVenueBookingPage calls it — rather than restating it here. The
 * calendar can show a free 23:00 hour; the form offers starts only up to
 * 22:00, so a prefill starting at 23:00 would land on a start time the form
 * does not list. One rule, one place: if the form's hours change, this
 * follows.
 */
export const slotFitsVenueForm = (from: string, to: string, timeZone: string = GRID_TZ): boolean => {
    const prefill = parseSlotPrefill(new URLSearchParams({
        [SLOT_PREFILL_PARAMS.assetId]: 'fit-check',
        [SLOT_PREFILL_PARAMS.from]: from,
        [SLOT_PREFILL_PARAMS.to]: to,
    }), timeZone);
    if (!prefill) return false;
    const { availableStartTimes, availableEndTimes } = getAvailableBookingTimes(
        prefill.startTime,
        dayjs(prefill.startDate),
        dayjs(prefill.endDate),
        timeZone,
    );
    return availableStartTimes.includes(prefill.startTime)
        && availableEndTimes.includes(prefill.endTime);
};
